import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArticleIcon from "@mui/icons-material/Article";
import ProjectIcon from "@mui/icons-material/Assignment";
import BusinessIcon from "@mui/icons-material/Business";
import FactoryIcon from "@mui/icons-material/Factory";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import type { EdgeDetail } from "../../../services/graphService";

interface TruncatedDescriptionProps {
  text: string;
  maxLength?: number;
}

const TruncatedDescription: React.FC<TruncatedDescriptionProps> = ({
  text,
  maxLength = 350,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (text.length <= maxLength) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {text}
      </Typography>
    );
  }

  const truncatedText = text.substring(0, maxLength).trim() + "...";

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {isExpanded ? text : truncatedText}{" "}
      <Link
        component="button"
        variant="body2"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          cursor: "pointer",
          textDecoration: "none",
          color: "primary.main",
          fontWeight: 500,
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {isExpanded ? "show less" : "show more"}
      </Link>
    </Typography>
  );
};

interface EdgeDetailProps {
  edgeDetail: EdgeDetail;
  onEntityClick?: (entityId: string) => void;
}

const getEntityIcon = (type: string) => {
  switch (type) {
    case "o":
      return <BusinessIcon sx={{ fontSize: 20 }} />;
    case "f":
      return <FactoryIcon sx={{ fontSize: 20 }} />;
    case "p":
      return <ProjectIcon sx={{ fontSize: 20 }} />;
    case "s":
      return <ArticleIcon sx={{ fontSize: 20 }} />;
    default:
      return <BusinessIcon sx={{ fontSize: 20 }} />;
  }
};

const EdgeDetailComponent: React.FC<EdgeDetailProps> = ({
  edgeDetail,
  onEntityClick,
}) => {
  const handleEntityClick = (entityId: string) => {
    if (onEntityClick) {
      onEntityClick(entityId);
    }
  };

  const renderAttributeCard = (
    title: string,
    icon: React.ReactNode,
    items: Array<{ label: string; value: string | number | undefined }>
  ) => {
    const validItems = items.filter(
      (item) =>
        item.value !== undefined && item.value !== null && item.value !== ""
    );

    if (validItems.length === 0) return null;

    return (
      <Grid item xs={12} md={6}>
        <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            {icon}
            <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          {validItems.map((item, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                {item.label}:
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Grid>
    );
  };

  return (
    <Box sx={{ px: 1, py: 0, flexShrink: 0 }}>
      {/* Connected Entities Section */}
      <Box sx={{ mb: 2 }}>
        {/* <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
          Connected Entities
        </Typography> */}

        {/* Two-column entity display */}
        <Grid container spacing={2}>
          {/* Source Entity */}
          <Grid item xs={5}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  {getEntityIcon(edgeDetail.source_entity.type)}
                  <Typography
                    variant="subtitle2"
                    sx={{ ml: 1, color: "text.secondary" }}
                  >
                    Source {edgeDetail.source_entity.labels}
                  </Typography>
                </Box>

                <Typography
                  variant="h6"
                  gutterBottom
                  onClick={() => handleEntityClick(edgeDetail.source_entity.id)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "primary.dark",
                    },
                  }}
                >
                  {edgeDetail.source_entity.name}
                </Typography>

                {edgeDetail.source_entity.description && (
                  <TruncatedDescription
                    text={edgeDetail.source_entity.description}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Arrow */}
          <Grid
            item
            xs={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                mx: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: "text.secondary", fontWeight: "medium" }}
              >
                {edgeDetail.relation_type}
              </Typography>
            </Box>
          </Grid>

          {/* Target Entity */}
          <Grid item xs={5}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  {getEntityIcon(edgeDetail.target_entity.type)}
                  <Typography
                    variant="subtitle2"
                    sx={{ ml: 1, color: "text.secondary" }}
                  >
                    Target {edgeDetail.target_entity.labels}
                  </Typography>
                </Box>

                <Typography
                  variant="h6"
                  gutterBottom
                  onClick={() => handleEntityClick(edgeDetail.target_entity.id)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "primary.dark",
                    },
                  }}
                >
                  {edgeDetail.target_entity.name}
                </Typography>

                {edgeDetail.target_entity.description && (
                  <TruncatedDescription
                    text={edgeDetail.target_entity.description}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Relationship Attributes Section */}
      <Box sx={{ mb: 2 }}>
        {/* <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
          Relationship Attributes
        </Typography>
 */}
        <Grid container spacing={2}>
          {/* Financial Details */}
          {renderAttributeCard(
            "Financial Details",
            <TrendingUpIcon sx={{ fontSize: 20, color: "primary.main" }} />,
            [
              {
                label: "Investment Amount",
                value: edgeDetail.investment_display,
              },
              {
                label: "Ownership Percentage",
                value: edgeDetail.ownership_percentage,
              },
            ]
          )}

          {/* Timeline */}
          {renderAttributeCard(
            "Timeline",
            <ScheduleIcon sx={{ fontSize: 20, color: "primary.main" }} />,
            [
              { label: "Relationship Start", value: edgeDetail.relation_start },
              { label: "Relationship End", value: edgeDetail.relation_end },
            ]
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default EdgeDetailComponent;
